import { createRouter, createWebHistory, type RouteLocationNormalized } from 'vue-router'
import { useAuthStore } from '@/stores'
import { logNavigation, warnNavigation } from '@/utils/navigation-debug'
import { captureError } from '@/utils/sentry'

// Lazy load views
const LoginView = () => import('@/views/LoginView.vue')
const SettingsView = () => import('@/views/SettingsView.vue')
const DashboardView = () => import('@/views/DashboardView.vue')
const FamilyView = () => import('@/views/FamilyView.vue')
const ChoresView = () => import('@/views/ChoresView.vue')
const RewardsView = () => import('@/views/RewardsView.vue')
const ReviewView = () => import('@/views/ReviewView.vue')

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition
    if (to.path !== from.path) return { top: 0 }
    return false
  },
  routes: [
    {
      path: '/',
      // The marketing site owns the public pitch.
      redirect: { name: 'login' }
    },
    {
      path: '/login',
      name: 'login',
      component: LoginView,
      beforeEnter: (to) => {
        const { token, exp, sig, redirect } = to.query
        if (typeof token === 'string' && typeof exp === 'string' && typeof sig === 'string') {
          return {
            name: 'kid-join',
            query: { token, exp, sig, ...(typeof redirect === 'string' ? { redirect } : {}) },
            replace: true
          }
        }
        return true
      },
      meta: { requiresAuth: false, guestOnly: true, hideAppBar: true }
    },
    {
      path: '/join',
      name: 'kid-join',
      component: () => import('@/views/KidJoinView.vue'),
      meta: { requiresAuth: false, guestOnly: true }
    },
    {
      path: '/dashboard',
      name: 'dashboard',
      component: DashboardView,
      meta: { requiresAuth: true, title: 'Dashboard' }
    },
    {
      path: '/family',
      name: 'family',
      component: FamilyView,
      meta: { requiresAuth: true, requiresAdult: true, title: 'Family' }
    },
    {
      path: '/chores',
      name: 'chores',
      component: ChoresView,
      meta: { requiresAuth: true, title: 'Chores' }
    },
    {
      // Push notifications deep-link to /chores/<id> (API handlers chore.go / chore_completion.go).
      // There is no chore detail view yet, so land on the chores list.
      path: '/chores/:choreId',
      redirect: '/chores'
    },
    {
      path: '/rewards',
      name: 'rewards',
      component: RewardsView,
      meta: { requiresAuth: true, title: 'Rewards' }
    },
    {
      path: '/review',
      name: 'review',
      component: ReviewView,
      meta: { requiresAuth: true, requiresAdult: true, title: 'Review Queue' }
    },
    {
      path: '/settings',
      name: 'settings',
      component: SettingsView,
      meta: { requiresAuth: true, title: 'Settings' }
    },
    {
      path: '/tasks',
      name: 'TaskLibrary',
      component: () => import('../views/TaskLibrary.vue'),
      meta: { requiresAuth: true, requiresAdult: true, title: 'Task Library', backTo: '/chores' }
    },
    {
      path: '/invitations/:token',
      name: 'accept-invitation',
      component: () => import('@/views/AcceptInvitationView.vue'),
      meta: { requiresAuth: false, title: 'Accept Invitation' }
    },
    {
      path: '/check-email',
      name: 'check-email',
      component: () => import('@/views/CheckEmailView.vue'),
      meta: { requiresAuth: false, guestOnly: true }
    },
    {
      path: '/register',
      name: 'register',
      component: () => import('@/views/RegisterView.vue'),
      meta: { requiresAuth: false, guestOnly: true, hideAppBar: true }
    },
    {
      path: '/verify-email',
      name: 'verify-email',
      component: () => import('@/views/VerifyEmailView.vue'),
      props: (route) => ({ token: typeof route.query.token === 'string' ? route.query.token : '' }),
      meta: { requiresAuth: false }
    },
    {
      path: '/admin',
      name: 'admin',
      component: () => import('@/views/AdminView.vue'),
      meta: { requiresAuth: true, requiresAdmin: true, title: 'Admin' }
    },
    {
      path: '/account-deletion',
      name: 'account-deletion',
      component: () => import('@/views/AccountDeletionInfoView.vue'),
      meta: { requiresAuth: false, title: 'Delete Your Account' }
    },
    // Keep unknown URLs visible as public 404 pages.
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('@/views/NotFoundView.vue'),
      meta: { requiresAuth: false, title: 'Page not found' }
    }
  ]
})

// Navigation guard
router.beforeEach(async (to: RouteLocationNormalized, from: RouteLocationNormalized) => {
  logNavigation({
    prefix: '[Router]',
    event: 'beforeEach:entry',
    from: from.fullPath,
    to: to.fullPath,
    metadata: { toName: to.name, requiresAuth: to.meta.requiresAuth, guestOnly: to.meta.guestOnly }
  })

  const authStore = useAuthStore()

  // Initialize auth state if not done
  if (!authStore.isInitialized) {
    logNavigation({ prefix: '[Router]', event: 'beforeEach:authInit:start', to: to.fullPath })
    await authStore.initialize()
    logNavigation({ prefix: '[Router]', event: 'beforeEach:authInit:complete', to: to.fullPath, metadata: { isAuthenticated: authStore.isAuthenticated } })
  }

  const requiresAuth = to.meta.requiresAuth !== false
  const guestOnly = to.meta.guestOnly === true

  if (requiresAuth && !authStore.isAuthenticated) {
    // Redirect to login if not authenticated
    logNavigation({ prefix: '[Router]', event: 'beforeEach:redirect', from: to.fullPath, to: '/login', reason: 'unauthenticated user accessing protected route' })
    return { name: 'login', query: { redirect: to.fullPath } }
  }

  // Safety net: authenticated but no account role means broken state slipped past initialize().
  if (requiresAuth && authStore.isAuthenticated && !authStore.currentAccountRole) {
    logNavigation({
      prefix: '[Router]',
      event: 'beforeEach:redirect',
      from: to.fullPath,
      to: '/login',
      reason: 'authenticated but no account role (broken state safety net)',
    })
    return { name: 'login' }
  }

  if (guestOnly && authStore.isAuthenticated && authStore.currentAccountRole) {
    // Redirect away from guest-only pages if already authenticated
    logNavigation({ prefix: '[Router]', event: 'beforeEach:redirect', from: to.fullPath, to: '/dashboard', reason: 'authenticated user accessing guest-only route' })
    return { name: 'dashboard' }
  }

  // Check adult requirement
  const requiresAdult = to.meta.requiresAdult === true
  if (requiresAdult && !authStore.isAdult) {
    logNavigation({
      prefix: '[Router]',
      event: 'beforeEach:redirect',
      from: to.fullPath,
      to: '/dashboard',
      reason: 'child user accessing adult-only route'
    })
    return { name: 'dashboard', query: { error: 'access_denied' } }
  }

  // Check admin requirement
  const requiresAdmin = to.meta.requiresAdmin === true
  if (requiresAdmin && !authStore.isAdmin) {
    logNavigation({ prefix: '[Router]', event: 'beforeEach:redirect', from: to.fullPath, to: '/dashboard', reason: 'non-admin user accessing admin-only route' })
    return { name: 'dashboard', query: { error: 'access_denied' } }
  }

  return true
})

router.afterEach((to, from) => {
  logNavigation({
    prefix: '[Router]',
    event: 'afterEach:complete',
    from: from.fullPath,
    to: to.fullPath,
  })
})

router.onError((error, to, from) => {
  const errorObj = error instanceof Error ? error : new Error(String(error))

  warnNavigation({
    prefix: '[Router]',
    event: 'onError',
    from: from?.fullPath,
    to: to?.fullPath,
    reason: errorObj.message,
    metadata: { error: errorObj.name }
  })
  captureError(errorObj, {
    source: 'router.onError',
    to: to?.fullPath,
    from: from?.fullPath,
  })
})

let unauthorizedPromise: Promise<void> | null = null

export function handleUnauthorized(): Promise<void> {
  if (unauthorizedPromise) return unauthorizedPromise

  unauthorizedPromise = (async () => {
    await useAuthStore().resetSession()

    const current = router.currentRoute.value
    if (current.name === 'login') {
      logNavigation({ prefix: '[Router]', event: 'handleUnauthorized:alreadyOnLogin', to: current.fullPath })
      return
    }

    const target = current.meta.requiresAuth !== false
      ? { name: 'login', query: { redirect: current.fullPath } }
      : { name: 'login' }
    warnNavigation({
      prefix: '[Router]',
      event: 'handleUnauthorized:redirect',
      from: current.fullPath,
      to: '/login',
      reason: '401 Unauthorized'
    })
    const failure = await router.push(target)
    if (failure) throw failure
  })().catch((error: unknown) => {
    const errorObj = error instanceof Error ? error : new Error(String(error))
    warnNavigation({
      prefix: '[Router]',
      event: 'handleUnauthorized:error',
      from: router.currentRoute.value.fullPath,
      to: '/login',
      reason: errorObj.message
    })
    captureError(errorObj, { source: 'router.handleUnauthorized' })
  }).finally(() => {
    unauthorizedPromise = null
  })

  return unauthorizedPromise
}

export async function revalidateCurrentRoute(): Promise<void> {
  const current = router.currentRoute.value
  logNavigation({
    prefix: '[Router]',
    event: 'revalidateCurrentRoute',
    from: current.fullPath,
    to: current.fullPath
  })
  try {
    const failure = await router.push({
      path: current.path,
      query: current.query,
      hash: current.hash,
      force: true,
      replace: true
    })
    if (failure) throw failure
  } catch (error) {
    const errorObj = error instanceof Error ? error : new Error(String(error))
    warnNavigation({
      prefix: '[Router]',
      event: 'revalidateCurrentRoute:error',
      from: current.fullPath,
      to: current.fullPath,
      reason: errorObj.message
    })
    captureError(errorObj, { source: 'router.revalidateCurrentRoute', to: current.fullPath })
  }
}

export default router
